import { useEffect } from 'react';
import { useSiteSettings } from '../context/SiteSettingsContext';

export function SEOHead({
  title = "The Hotel Emerald | Luxury Hotel in Semiliguda, Odisha",
  description = "Experience luxury hospitality at The Hotel Emerald, a premium hotel nestled in the scenic Eastern Ghats of Semiliguda, Odisha. Book your stay today.",
  keywords = "hotel semiliguda, luxury hotel odisha, hotel emerald, eastern ghats resort, koraput hotel, odisha tourism",
  image = "/images/gallery/hotel-1.jpeg",
  url = "https://thehotelemerald.in",
  type = "website"
}) {
  const { settings } = useSiteSettings();
  const hotelName = settings?.seo?.hotel_name || 'The Hotel Emerald';
  
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard meta tags
    updateMeta('description', description);
    updateMeta('keywords', keywords);

    // Open Graph tags
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:image', image, true);
    updateMeta('og:url', url, true);
    updateMeta('og:type', type, true);
    updateMeta('og:site_name', hotelName, true);

    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', image);

    // Cleanup function
    return () => {
      document.title = hotelName;
    };
  }, [title, description, keywords, image, url, type, hotelName]);

  return null;
}

export function HotelSchema() {
  const { settings } = useSiteSettings();
  const seo = settings?.seo || {};
  const contact = settings?.contact || {};
  const maps = settings?.google_maps || {};
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "name": seo.hotel_name || "The Hotel Emerald",
    "description": `A premium ${seo.star_rating || '4'}-star hotel nestled in the scenic Eastern Ghats of Semiliguda, Odisha, offering world-class hospitality and breathtaking mountain views.`,
    "url": seo.website_url || "https://thehotelemerald.in",
    "telephone": contact.phones?.[0] || "+91-6372-350-065",
    "email": contact.email || "thehotelemerald@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contact.address?.split(',')[0] || "NALCO Chowk, Semiliguda",
      "addressLocality": "Koraput",
      "addressRegion": "Odisha",
      "postalCode": "764036",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": maps.latitude || "18.87",
      "longitude": maps.longitude || "82.82"
    },
    "image": [
      `${seo.website_url || 'https://thehotelemerald.in'}/images/gallery/hotel-1.jpeg`,
      `${seo.website_url || 'https://thehotelemerald.in'}/images/gallery/hotel-2.jpeg`
    ],
    "priceRange": seo.price_range || "₹₹₹",
    "starRating": {
      "@type": "Rating",
      "ratingValue": seo.star_rating || "4"
    },
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Free WiFi", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Restaurant", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Room Service", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Air Conditioning", "value": true }
    ],
    "checkinTime": seo.check_in_time || "14:00",
    "checkoutTime": seo.check_out_time || "11:00",
    "currenciesAccepted": "INR",
    "paymentAccepted": seo.payment_methods || ["Cash", "Credit Card", "UPI"],
    "availableLanguage": seo.languages_spoken || ["English", "Hindi", "Odia"]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'hotel-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('hotel-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('hotel-schema');
      if (s) s.remove();
    };
  }, [schema]);

  return null;
}

export function RoomSchema({ room }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    "name": room.name,
    "description": room.description,
    "image": room.images[0],
    "occupancy": {
      "@type": "QuantitativeValue",
      "maxValue": room.features?.guests || 2
    },
    "bed": {
      "@type": "BedDetails",
      "numberOfBeds": room.features?.beds || 1,
      "typeOfBed": room.features?.bedType || "King"
    },
    "amenityFeature": room.amenities?.map(amenity => ({
      "@type": "LocationFeatureSpecification",
      "name": amenity,
      "value": true
    })),
    "offers": {
      "@type": "Offer",
      "price": room.price,
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock"
    }
  };

  useEffect(() => {
    const scriptId = `room-schema-${room.id}`;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [room]);

  return null;
}

export function LocalBusinessSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Hotel Emerald",
    "image": "https://thehotelemerald.in/images/gallery/hotel-1.jpeg",
    "@id": "https://thehotelemerald.in",
    "url": "https://thehotelemerald.in",
    "telephone": "+91-6372-350-065",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "NALCO Chowk, Semiliguda",
      "addressLocality": "Koraput",
      "addressRegion": "Odisha",
      "postalCode": "764036",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "18.87",
      "longitude": "82.82"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "sameAs": [
      "https://www.instagram.com/thehotelemerald",
      "https://www.facebook.com/thehotelemerald"
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'local-business-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('local-business-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('local-business-schema');
      if (s) s.remove();
    };
  }, []);

  return null;
}

export function EventVenueSchema() {
  const { settings } = useSiteSettings();
  const seo = settings?.seo || {};
  const contact = settings?.contact || {};
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "EventVenue",
    "name": `${seo.hotel_name || 'The Hotel Emerald'} - Events & Weddings`,
    "description": `Premier destination wedding and event venue in the Eastern Ghats of Odisha at ${seo.hotel_name || 'The Hotel Emerald'}`,
    "url": `${seo.website_url || 'https://thehotelemerald.in'}/events`,
    "telephone": contact.phones?.[0] || "+91-6372-350-065",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contact.address?.split(',')[0] || "Semiliguda",
      "addressLocality": "Koraput",
      "addressRegion": "Odisha",
      "postalCode": "764036",
      "addressCountry": "IN"
    },
    "maximumAttendeeCapacity": 500,
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Catering", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Audio/Visual Equipment", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Outdoor Space", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Parking", "value": true }
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'event-venue-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('event-venue-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('event-venue-schema');
      if (s) s.remove();
    };
  }, [schema]);

  return null;
}

export function TouristAttractionSchema({ attractions }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Tourist Attractions near The Hotel Emerald",
    "itemListElement": attractions.map((attraction, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "TouristAttraction",
        "name": attraction.name,
        "description": attraction.description
      }
    }))
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'attractions-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('attractions-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('attractions-schema');
      if (s) s.remove();
    };
  }, [attractions]);

  return null;
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'breadcrumb-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('breadcrumb-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('breadcrumb-schema');
      if (s) s.remove();
    };
  }, [items]);

  return null;
}

export function FAQSchema({ faqs }) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('faq-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('faq-schema');
      if (s) s.remove();
    };
  }, [faqs]);

  return null;
}

export function SpeakableSchema({ 
  cssSelector = [".speakable-content", "h1", "h2"],
  url = "https://thehotelemerald.in"
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "The Hotel Emerald",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": cssSelector
    },
    "url": url
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'speakable-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('speakable-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('speakable-schema');
      if (s) s.remove();
    };
  }, [cssSelector, url]);

  return null;
}

// Restaurant Schema for Dining page
export function RestaurantSchema({ restaurant }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "name": restaurant?.name || "Emerald Dining Room",
    "description": restaurant?.description || "Multi-cuisine restaurant at The Hotel Emerald serving Indian, Continental, and local Odia specialties",
    "image": restaurant?.image_url || "https://thehotelemerald.in/images/gallery/dining-1.jpeg",
    "servesCuisine": restaurant?.cuisine_type || ["Indian", "Continental", "Odia"],
    "priceRange": "₹₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "NALCO Chowk, Semiliguda",
      "addressLocality": "Koraput",
      "addressRegion": "Odisha",
      "postalCode": "764036",
      "addressCountry": "IN"
    },
    "telephone": "+91-6372-350-065",
    "url": "https://thehotelemerald.in/dining",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "07:00",
        "closes": "23:00"
      }
    ],
    "acceptsReservations": "True",
    "menu": "https://thehotelemerald.in/dining#menu",
    "hasMenu": {
      "@type": "Menu",
      "name": "The Hotel Emerald Menu",
      "description": "Multi-cuisine menu featuring Indian, Continental, and local Odia delicacies"
    }
  };

  useEffect(() => {
    const scriptId = restaurant?.id ? `restaurant-schema-${restaurant.id}` : 'restaurant-schema';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [restaurant]);

  return null;
}

// Review Schema for Testimonials
export function AggregateRatingSchema({ reviews = [] }) {
  // Calculate aggregate rating from reviews
  const totalReviews = reviews.length || 25;
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + (r.rating || 5), 0) / reviews.length).toFixed(1)
    : "4.8";

  const schema = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "name": "The Hotel Emerald",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": avgRating,
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": totalReviews
    },
    "review": reviews.slice(0, 5).map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.name || "Guest"
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating || 5,
        "bestRating": "5"
      },
      "reviewBody": review.content || review.text || "",
      "datePublished": review.date || new Date().toISOString().split('T')[0]
    }))
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'aggregate-rating-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('aggregate-rating-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('aggregate-rating-schema');
      if (s) s.remove();
    };
  }, [reviews]);

  return null;
}

// Offer/Special Deal Schema
export function OfferSchema({ offers = [] }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Special Offers at The Hotel Emerald",
    "itemListElement": offers.map((offer, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Offer",
        "name": offer.name || offer.title,
        "description": offer.description,
        "price": offer.price || offer.discounted_price,
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
        "validFrom": offer.valid_from || new Date().toISOString(),
        "validThrough": offer.valid_until || new Date(Date.now() + 90*24*60*60*1000).toISOString(),
        "seller": {
          "@type": "Hotel",
          "name": "The Hotel Emerald"
        }
      }
    }))
  };

  useEffect(() => {
    if (!offers || offers.length === 0) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'offers-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('offers-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('offers-schema');
      if (s) s.remove();
    };
  }, [offers]);

  return null;
}

// Booking Action Schema - for voice assistants
export function BookingActionSchema() {
  const { settings } = useSiteSettings();
  const seo = settings?.seo || {};
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": seo.hotel_name || "The Hotel Emerald",
    "url": seo.website_url || "https://thehotelemerald.in",
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": seo.booking_url || "https://thehotelemerald.in/book",
        "inLanguage": "en",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      },
      "result": {
        "@type": "LodgingReservation",
        "name": `Room Reservation at ${seo.hotel_name || 'The Hotel Emerald'}`
      }
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'booking-action-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('booking-action-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('booking-action-schema');
      if (s) s.remove();
    };
  }, [schema]);

  return null;
}

// Organization Schema with enhanced social proof
export function OrganizationSchema() {
  const { settings } = useSiteSettings();
  const seo = settings?.seo || {};
  const contact = settings?.contact || {};
  
  const sameAsLinks = [
    seo.instagram_url,
    seo.facebook_url,
    seo.tripadvisor_url
  ].filter(Boolean);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": seo.hotel_name || "The Hotel Emerald",
    "alternateName": `${seo.hotel_name || 'Hotel Emerald'} Semiliguda`,
    "url": seo.website_url || "https://thehotelemerald.in",
    "logo": `${seo.website_url || 'https://thehotelemerald.in'}/images/logo.png`,
    "description": `Premium ${seo.star_rating || '4'}-star hotel in the Eastern Ghats of Odisha offering world-class hospitality, stunning mountain views, and authentic local experiences.`,
    "foundingDate": seo.founding_year || "2020",
    "founders": [{
      "@type": "Person",
      "name": `${seo.hotel_name || 'The Hotel Emerald'} Group`
    }],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contact.address?.split(',')[0] || "NALCO Chowk, Semiliguda",
      "addressLocality": "Koraput",
      "addressRegion": "Odisha",
      "postalCode": "764036",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": contact.phones?.[0] || "+91-6372-350-065",
        "contactType": "reservations",
        "availableLanguage": seo.languages_spoken || ["English", "Hindi", "Odia"],
        "areaServed": "IN"
      },
      ...(contact.phones?.[1] ? [{
        "@type": "ContactPoint",
        "telephone": contact.phones[1],
        "contactType": "customer service",
        "availableLanguage": seo.languages_spoken || ["English", "Hindi", "Odia"]
      }] : [])
    ],
    "sameAs": sameAsLinks.length > 0 ? sameAsLinks : [
      "https://www.instagram.com/thehotelemerald",
      "https://www.facebook.com/thehotelemerald"
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'organization-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('organization-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('organization-schema');
      if (s) s.remove();
    };
  }, [schema]);

  return null;
}

// Place Schema for location-based searches
export function PlaceSchema() {
  const { settings } = useSiteSettings();
  const seo = settings?.seo || {};
  const contact = settings?.contact || {};
  const maps = settings?.google_maps || {};
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": seo.hotel_name || "The Hotel Emerald",
    "description": `${seo.hotel_tagline || 'Luxury hotel'} in Semiliguda, Odisha, surrounded by the scenic Eastern Ghats mountains`,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": maps.latitude || "18.87",
      "longitude": maps.longitude || "82.82"
    },
    "hasMap": maps.directions_url || `https://www.google.com/maps?q=${encodeURIComponent(seo.hotel_name || 'The Hotel Emerald')}+Semiliguda`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contact.address?.split(',')[0] || "NALCO Chowk, Semiliguda",
      "addressLocality": "Koraput",
      "addressRegion": "Odisha",
      "postalCode": "764036",
      "addressCountry": "IN"
    },
    "containedInPlace": {
      "@type": "AdministrativeArea",
      "name": "Eastern Ghats, Odisha"
    },
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Mountain Views", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Near Tribal Villages", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Coffee Plantations Nearby", "value": true }
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'place-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('place-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('place-schema');
      if (s) s.remove();
    };
  }, [schema]);

  return null;
}

// WebSite Schema with SearchAction for site search
export function WebSiteSchema() {
  const { settings } = useSiteSettings();
  const seo = settings?.seo || {};
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": seo.hotel_name || "The Hotel Emerald",
    "alternateName": `${seo.hotel_name || 'Hotel Emerald'} Semiliguda`,
    "url": seo.website_url || "https://thehotelemerald.in",
    "description": `Official website of ${seo.hotel_name || 'The Hotel Emerald'} - ${seo.hotel_tagline || 'Luxury hotel in Eastern Ghats, Odisha'}`,
    "inLanguage": "en",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${seo.website_url || 'https://thehotelemerald.in'}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'website-schema';
    script.innerHTML = JSON.stringify(schema);
    
    const existingScript = document.getElementById('website-schema');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('website-schema');
      if (s) s.remove();
    };
  }, [schema]);

  return null;
}

